import {
  getDdsSimplifiedStatus,
  isEudrErrorStatus,
  isEudrManuallyMitigatedStatus,
  isEudrRiskDetectedStatus,
} from '@/utils/ddsProductionPlaceStatus'
import {
  IYB_HIGH_RISK_MIN,
  normalizeRiskStatusLabel,
  resolveIybRiskScore,
  classifyIybRiskScore,
} from '@/utils/iybRiskClassification'
import { isMitigationRevisionRequested } from './ddsMitigationNotification'

const SEVERITY_ORDER = ['GREEN', 'YELLOW', 'LIGHT_RED', 'DARK_RED']

export { resolveIybRiskScore, classifyIybRiskScore }

export function mapIybDistrictScore(row) {
  if (!row) return null
  return {
    kabProfileId: row.kabProfileId != null ? Number(row.kabProfileId) : null,
    regencyName: row.districtName ?? row.regencyName ?? 'Unknown Regency',
    compositeScore: row.compositeScore ?? null,
    eudrRiskStatus: row.eudrRiskStatus ?? null,
    indicators: row.indicators ?? null,
    dataYear: row.dataYear ?? null,
    importedAt: row.updatedAt ?? row.importedAt ?? null,
    firePrevention: row.firePrevention ?? row.indicators?.ind2_forestLandFires ?? null,
  }
}

/** Frozen risk snapshot stored when DDS was approved / compliance generated. */
export function getRiskSnapshotFromProductionPlace(item) {
  return item?.riskSnapshot
    || item?.diligenceReportProductionPlaceArray?.[0]?.riskSnapshot
    || null
}

export function mapRiskSnapshotToIybData(snapshot) {
  if (!snapshot) return null
  return {
    kabProfileId: snapshot.kabProfileId != null ? Number(snapshot.kabProfileId) : null,
    regencyName: snapshot.regencyName ?? 'Unknown Regency',
    compositeScore: snapshot.compositeScore ?? null,
    eudrRiskStatus: snapshot.eudrRiskStatus ?? null,
    indicators: snapshot.indicators ?? null,
    dataYear: snapshot.iybDataYear ?? null,
    importedAt: snapshot.snapshotAt ?? null,
    firePrevention: snapshot.firePrevention ?? snapshot.indicators?.ind2_forestLandFires ?? null,
    frozen: true,
  }
}

/** Regency id from farm registration (user_farms.kabProfileId). */
export function resolveFarmKabProfileId(farm) {
  if (farm?.kabProfileId == null || farm.kabProfileId === '') return null
  const id = Number(farm.kabProfileId)
  return Number.isNaN(id) ? null : id
}

export function collectKabProfileIdsFromProductionPlaces(rows = []) {
  return [...new Set(
    rows.map(item => resolveFarmKabProfileId(item?.farm)).filter(id => id != null)
  )]
}

export function normalizeCommodity(productName) {
  if (!productName) return null
  const normalized = String(productName).toUpperCase().replace(/[\s_-]/g, '')
  if (normalized.includes('PALM')) return 'PALM_OIL'
  if (normalized.includes('COCOA') || normalized.includes('CACAO')) return 'COCOA'
  if (normalized.includes('COFFEE')) return 'COFFEE'
  return String(productName).toUpperCase()
}

export function isPalmOilCommodity(commodity) {
  return (commodity || '').toUpperCase().replace(/[\s_-]/g, '') === 'PALMOIL'
}

export function getProductionPlaceDeforestationInfo(item) {
  if (!item) return null
  return item.deforestationInfo
    || item.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo
    || item.productionPlaceDeforestationInfo
    || null
}

/** Active temporary mitigation evidence approval — traffic light shows Compliant until this date. */
export function hasActiveMitigationTemporaryApproval(item) {
  const info = getProductionPlaceDeforestationInfo(item)
  if (!info?.mitigationTemporaryExpirationDate) return false
  return new Date(info.mitigationTemporaryExpirationDate).getTime() > Date.now()
}

/** Permanent manual mitigation (no expiry) — traffic light stays Compliant. */
export function isPermanentMitigationApproval(item) {
  if (isMitigationRevisionRequested(item)) return false;
  const info = getProductionPlaceDeforestationInfo(item)
  if (info?.mitigationTemporaryExpirationDate) return false

  const deforestationStatus = String(info?.deforestationStatus || item?.deforestationStatus || '').toLowerCase()
  if (deforestationStatus === 'compliant') return false
  if (deforestationStatus.includes('manually mitigated')) return true

  const eudrStatus = item?.eudr_deforestation_status || ''
  if (info?.lastDeforestationMitigationDate && isEudrZeroNegligibleStatus(eudrStatus)) {
    return true
  }

  return false
}

/** True when mitigation evidence was approved (temp or permanent). */
export function isMitigationEvidenceApproved(item) {
  if (hasActiveMitigationTemporaryApproval(item)) return true
  if (isPermanentMitigationApproval(item)) return true
  const info = getProductionPlaceDeforestationInfo(item)
  const status = String(
    info?.deforestationStatus || item?.eudr_deforestation_status || item?.deforestationStatus || '',
  ).toLowerCase()

  if (isEudrManuallyMitigatedStatus(status)) return true

  const isZeroNegligible = isEudrZeroNegligibleStatus(status);
  if (!isZeroNegligible) return false;
  const mitigationComment = String(info?.deforestationMitigationComment || '').toLowerCase();
  const mitigationExpiryDate = info?.mitigationTemporaryExpirationDate;
  if (!mitigationExpiryDate && mitigationComment === 'mitigation evidence approved') {
    return true;
  } else if (mitigationExpiryDate && new Date(mitigationExpiryDate).getTime() > Date.now() && mitigationComment.startsWith('temporary mitigation evidence approval for')) {
    return true;
  }
  return false;
}

function pickWorstTrafficLight(...lights) {
  let worst = 'GREEN'
  for (const light of lights) {
    if (!light) continue
    if (SEVERITY_ORDER.indexOf(light) > SEVERITY_ORDER.indexOf(worst)) {
      worst = light
    }
  }
  return worst
}

export function trafficLightFromDeforestationStatus(item) {
  const status = (
    item?.eudr_deforestation_status
    || item?.deforestationStatus
    || item?.deforestationInfo?.deforestationStatus
    || item?.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo?.deforestationStatus
    || item?.overallHistoryStatus
    || ''
  ).toLowerCase()

  if (!status) return null
  if (status === 'rejected' || status.includes('very high') || status === 'non-compliant') return 'DARK_RED'
  if (status.includes('high deforestation') || status === 'high_risk' || status === 'deforestation_detected') {
    return 'LIGHT_RED'
  }
  if (status.includes('medium deforestation') || status === 'medium probability') {
    return 'LIGHT_RED'
  }
  if (
    status.includes('manually mitigated')
    || status.includes('zero')
    || status.includes('negligible')
    || status.includes('compliant')
    || status === 'approved'
  ) {
    return 'GREEN'
  }
  if (status.includes('low deforestation') || status.includes('mitigation')) {
    return 'YELLOW'
  }
  return null
}

export function trafficLightFromIybData(iybData) {
  if (!iybData) return null

  // Prefer composite score (same as IYBTrafficLightPanel gauge badge).
  const score = resolveIybRiskScore(iybData)
  const derivedStatus = classifyIybRiskScore(score)
  if (derivedStatus === 'Low Risk') return 'GREEN'
  if (derivedStatus === 'Medium Risk') return 'YELLOW'
  if (derivedStatus === 'High Risk') return 'LIGHT_RED'
  if (derivedStatus === 'Critical Risk') return 'DARK_RED'

  const statusLabel = normalizeRiskStatusLabel(iybData.eudrRiskStatus)
  if (statusLabel === 'Low Risk') return 'GREEN'
  if (statusLabel === 'Medium Risk') return 'YELLOW'
  if (statusLabel === 'High Risk') return 'LIGHT_RED'
  if (statusLabel === 'Critical Risk') return 'DARK_RED'
  return null
}

export function derivePhase1FromProductionPlace(item) {
  const deforestationStatus = (
    item?.eudr_deforestation_status
    || item?.deforestationInfo?.deforestationStatus
    || item?.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo?.deforestationStatus
    || ''
  ).toLowerCase()

  const pyData = item?.dueDiligenceProductionPlacesPyData

  const hasMeaningfulOverlap = (value) => {
    if (value == null) return false
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value > 0
    const normalized = String(value).trim().toLowerCase()
    if (!normalized) return false
    if (['false', '0', 'no', 'none', 'null', 'n/a', 'na', 'not found'].includes(normalized)) {
      return false
    }
    if (normalized.includes('no overlap') || normalized.includes('outside')) {
      return false
    }
    if (['true', '1', 'yes'].includes(normalized)) return true
    return normalized.includes('overlap')
      || normalized.includes('inside')
      || normalized.includes('intersect')
      || normalized.includes('detected')
  }

  return {
    deforestation:
      deforestationStatus.includes('very high')
      || deforestationStatus.includes('high deforestation')
      || deforestationStatus === 'non-compliant',
    forestOverlap:
      hasMeaningfulOverlap(pyData?.protectedArea)
      || hasMeaningfulOverlap(pyData?.indigenousArea),
    bufferZone:
      deforestationStatus.includes('medium')
      || deforestationStatus.includes('low deforestation'),
  }
}

export function trafficLightFromPhase1(phase1) {
  if (!phase1) return null
  if (phase1.deforestation || phase1.forestOverlap) return 'DARK_RED'
  if (phase1.bufferZone) return 'YELLOW'
  return null
}

export function trafficLightFromFirePrevention(iybData, commodity) {
  // Step 3/4 fire-prevention rule applies only to palm oil.
  if (!isPalmOilCommodity(commodity) || !iybData) return null
  const value = iybData.firePrevention ?? iybData.indicators?.ind2_forestLandFires
  if (value == null || Number.isNaN(Number(value))) return null
  if (Number(value) <= 40) return 'DARK_RED'
  if (Number(value) < IYB_HIGH_RISK_MIN) return 'YELLOW'
  return null
}

export function calculateFarmTrafficLight({ item, iybData, commodity } = {}) {
  // Temporary mitigation approval: show Compliant (green) while approval is active.
  // After expiry, fall back to IYB-based risk (e.g. yellow for medium-risk farms).
  if (item) {
    if (hasActiveMitigationTemporaryApproval(item)) return 'GREEN'
    if (isPermanentMitigationApproval(item)) return 'GREEN'
  }

  // Traffic light is driven by IYB district score/risk plus
  // palm-oil-only fire-prevention risk (Step 3/4).
  return pickWorstTrafficLight(
    trafficLightFromIybData(iybData),
    trafficLightFromFirePrevention(iybData, commodity),
  )
}

/** Local DB traffic light from IYB/fire-prevention only — ignores mitigation overrides. */
export function getBaselineFarmTrafficLight(item) {
  const riskSnapshot = getRiskSnapshotFromProductionPlace(item)
  const iybData = item?.iybData ?? (riskSnapshot ? mapRiskSnapshotToIybData(riskSnapshot) : null)
  const commodity = item?.commodity
  return pickWorstTrafficLight(
    trafficLightFromIybData(iybData),
    trafficLightFromFirePrevention(iybData, commodity),
  )
}

const TRAFFIC_LIGHT_LABEL_KEYS = {
  GREEN: 'deforestation.compliant',
  YELLOW: 'deforestation.combinedComplianceMediumRisk',
  LIGHT_RED: 'deforestation.riskStatusHigh',
  DARK_RED: 'deforestation.complianceTrafficLightLegendCriticalRisk',
}

const TRAFFIC_LIGHT_LABEL_FALLBACKS = {
  GREEN: 'Compliant',
  YELLOW: 'Medium Risk',
  LIGHT_RED: 'High Risk',
  DARK_RED: 'Critical Risk',
}

export function trafficLightLabelFromState(tl, t) {
  const translate = typeof t === 'function' ? t : null
  if (TRAFFIC_LIGHT_LABEL_KEYS[tl]) {
    const key = TRAFFIC_LIGHT_LABEL_KEYS[tl]
    return translate ? translate(key) : TRAFFIC_LIGHT_LABEL_FALLBACKS[tl]
  }
  return translate
    ? translate('deforestation.trafficLightNotAvailable')
    : 'Not Available'
}

export function trafficLightBadgeClassFromState(tl) {
  if (tl === 'GREEN') return 'green-button'
  if (tl === 'YELLOW') return 'orange-button'
  if (tl === 'LIGHT_RED' || tl === 'DARK_RED') return 'red-button'
  return 'gray-button'
}

export function trafficLightColorFromState(tl) {
  if (tl === 'GREEN') return '#489E16'
  if (tl === 'YELLOW') return '#FFA826'
  if (tl === 'LIGHT_RED') return '#DA3D3D'
  if (tl === 'DARK_RED') return '#7B0000'
  return '#9e9e9e'
}

export function trafficLightIconFromState(tl) {
  if (tl === 'GREEN') return 'mdi-check-circle'
  if (tl === 'YELLOW') return 'mdi-alert-circle'
  if (tl === 'LIGHT_RED') return 'mdi-close-circle'
  if (tl === 'DARK_RED') return 'mdi-close-octagon'
  return 'mdi-help-circle-outline'
}

/** Aggregate per-report traffic light counts for list/summary views. */
export function summarizeProductionPlacesTrafficLight(places = []) {
  const counts = {
    green: 0,
    manualGreen: 0,
    yellow: 0,
    red: 0,
    darkRed: 0,
  }

  places.forEach((item) => {
    const tl = item.computedTrafficLight
      || calculateFarmTrafficLight({
        item,
        iybData: item.iybData,
        commodity: item.commodity,
      })

    const isManual = hasActiveMitigationTemporaryApproval(item)
      || isPermanentMitigationApproval(item)

    if (tl === 'GREEN') {
      if (isManual) counts.manualGreen += 1
      else counts.green += 1
    } else if (tl === 'YELLOW') {
      counts.yellow += 1
    } else if (tl === 'LIGHT_RED') {
      counts.red += 1
    } else if (tl === 'DARK_RED') {
      counts.darkRed += 1
    } else {
      counts.yellow += 1
    }
  })

  return counts
}

const EUDR_ZERO_NEGLIGIBLE_EXACT = new Set([
  'Zero/Negligible Deforestation Probability',
  'Zero/Negligible Probability',
  'Zero/Negligible Risk',
  'Compliant',
  'Very Low Deforestation Probability',
  'Very Low Probability',
  'Manually Mitigated',
])

export function getProductionPlaceEudrStatus(item) {
  const deforestationInfo = getProductionPlaceDeforestationInfo(item)
  const infoStatus = deforestationInfo?.deforestationStatus
  if (infoStatus && isEudrManuallyMitigatedStatus(infoStatus)) {
    return infoStatus
  }
  if (hasActiveMitigationTemporaryApproval(item) && infoStatus) {
    return infoStatus
  }
  return item?.eudr_deforestation_status
    || item?.deforestationStatus
    || infoStatus
    || deforestationInfo?.deforestationReport?.errorStatus
    || item?.errorStatus
    || ''
}

export function getProductionPlaceTrafficLight(item) {
  if (item?.computedTrafficLight) return item.computedTrafficLight
  return calculateFarmTrafficLight({
    item,
    iybData: item?.iybData,
    phase1: item?.phase1,
    commodity: item?.commodity,
  })
}

/** EUDR assessment is zero/negligible (green side). */
export function isEudrZeroNegligibleStatus(rawStatus) {
  if (!rawStatus) return false
  const status = String(rawStatus).trim()
  if (EUDR_ZERO_NEGLIGIBLE_EXACT.has(status)) return true
  const normalized = status.toLowerCase()
  if (normalized.includes('zero') && normalized.includes('negligible')) return true
  if (normalized.includes('very low')) return true
  if (normalized === 'compliant') return true
  if (normalized.includes('manually mitigated')) return true
  return false
}

const RISK_TIER_SEVERITY = {
  none: 0,
  compliant: 1,
  low: 2,
  medium: 3,
  high: 4,
}

function riskTierSeverity(tier) {
  return RISK_TIER_SEVERITY[tier] ?? 0
}

function displayFromStoredComplianceStatus(complianceStatus, translate) {
  const normalized = String(complianceStatus).trim().toLowerCase()
  if (normalized === 'compliant') {
    return {
      label: translate('deforestation.compliant'),
      badgeClass: 'green-button',
      riskTier: 'compliant',
    }
  }
  if (normalized.includes('manually mitigated')) {
    return {
      label: translate('dueDiligence.manualMitigated'),
      badgeClass: 'manually-button',
      riskTier: 'compliant',
    }
  }
  if (normalized.includes('critical')) {
    return {
      label: complianceStatus,
      badgeClass: 'red-button',
      riskTier: 'high',
    }
  }
  if (normalized.includes('high risk')) {
    return {
      label: translate('deforestation.riskStatusHigh'),
      badgeClass: 'red-button',
      riskTier: 'high',
    }
  }
  if (normalized.includes('risk detected')) {
    return {
      label: translate('deforestation.riskDetected'),
      badgeClass: 'red-button',
      riskTier: 'high',
    }
  }
  if (normalized.includes('medium')) {
    return {
      label: translate('deforestation.combinedComplianceMediumRisk'),
      badgeClass: 'orange-button',
      riskTier: 'medium',
    }
  }
  return {
    label: complianceStatus,
    badgeClass: 'gray-button',
    riskTier: 'none',
  }
}

function trafficLightToRiskTier(tl) {
  if (tl === 'DARK_RED' || tl === 'LIGHT_RED') return 'high'
  if (tl === 'YELLOW') return 'medium'
  if (tl === 'GREEN') return 'compliant'
  return 'none'
}

function eudrStatusToRiskTier(eudrStatus, options = {}) {
  if (!eudrStatus) return 'none'
  if (options?.indonesiaPtsi && isEudrManuallyMitigatedStatus(eudrStatus)) return 'high'
  const normalized = String(eudrStatus).toLowerCase()
  if (normalized.includes('manually mitigated')) return 'compliant'
  if (isEudrZeroNegligibleStatus(eudrStatus)) return 'compliant'
  if (isEudrRiskDetectedStatus(eudrStatus)) return 'high'
  if (isEudrErrorStatus(eudrStatus)) return 'medium'
  return 'high'
}

function displayFromEudrStatus(eudrStatus, translate, options = {}) {
  if (options?.indonesiaPtsi && isEudrManuallyMitigatedStatus(eudrStatus)) {
    return {
      label: translate('deforestation.riskDetected'),
      badgeClass: 'red-button',
      riskTier: 'high',
    }
  }

  const simplified = getDdsSimplifiedStatus(eudrStatus, translate)
  if (simplified) {
    return {
      label: simplified.text,
      badgeClass: simplified.class,
      riskTier: simplified.class === 'green-button' ? 'compliant' : 'high',
    }
  }

  const normalized = String(eudrStatus).toLowerCase()
  if (normalized.includes('manually mitigated')) {
    return {
      label: translate('dueDiligence.manualMitigated'),
      badgeClass: 'manually-button',
      riskTier: 'compliant',
    }
  }
  if (isEudrErrorStatus(eudrStatus)) {
    return {
      label: eudrStatus,
      badgeClass: 'deep-orange-darken-1-button',
      riskTier: 'medium',
    }
  }
  return {
    label: translate('deforestation.riskDetected'),
    badgeClass: 'red-button',
    riskTier: 'high',
  }
}

function displayFromTrafficLight(tl, translate) {
  if (tl === 'GREEN') {
    return {
      label: translate('deforestation.compliant'),
      badgeClass: 'green-button',
      riskTier: 'compliant',
    }
  }
  return {
    label: trafficLightLabelFromState(tl, translate),
    badgeClass: trafficLightBadgeClassFromState(tl),
    riskTier: trafficLightToRiskTier(tl),
  }
}

/**
 * Local DB compliance display when EUDR is zero/negligible.
 * Uses the actual Local DB traffic-light tier (no downgrade).
 */
export function getCombinedLocalDbComplianceDisplay(item, t, options = {}) {
  const eudrStatus = getProductionPlaceEudrStatus(item)
  if (options?.indonesiaPtsi && isEudrManuallyMitigatedStatus(eudrStatus)) return null
  if (!isEudrZeroNegligibleStatus(eudrStatus)) return null

  const translate = typeof t === 'function' ? t : (key) => key
  const tl = getProductionPlaceTrafficLight(item)
  return displayFromTrafficLight(tl, translate)
}

/**
 * Merged production-place status from Local DB traffic light + EUDR assessment.
 * The more critical status always wins (e.g. Local DB High Risk + EUDR zero/negligible -> High Risk).
 *
 * @param {object} [options]
 * @param {boolean} [options.indonesiaPtsi] — Manually mitigated EUDR counts as Risk Detected (high)
 */
export function getMergedProductionPlaceStatusDisplay(item, t, options = {}) {
  const translate = typeof t === 'function' ? t : (key) => key
  const eudrStatus = getProductionPlaceEudrStatus(item)
  const mitigationActive = isMitigationEvidenceApproved(item)

  // Evidence-approved farms always show Manually Mitigated in the compliance column.
  if (mitigationActive || isEudrManuallyMitigatedStatus(eudrStatus)) {
    return {
      label: translate('dueDiligence.manualMitigated'),
      badgeClass: 'manually-button',
      riskTier: 'compliant',
    }
  }

  const riskSnapshot = getRiskSnapshotFromProductionPlace(item)
  if (riskSnapshot?.complianceStatus) {
    return displayFromStoredComplianceStatus(riskSnapshot.complianceStatus, translate)
  }

  const baselineTl = getBaselineFarmTrafficLight(item)
  const tl = getProductionPlaceTrafficLight(item)

  const tlTier = trafficLightToRiskTier(tl)
  const eudrTier = eudrStatusToRiskTier(eudrStatus, options)
  const tlDisplay = displayFromTrafficLight(tl, translate)
  const eudrDisplay = eudrStatus ? displayFromEudrStatus(eudrStatus, translate, options) : null

  if (tlTier === 'compliant' && eudrTier === 'compliant') {
    return {
      label: translate('deforestation.compliant'),
      badgeClass: 'green-button',
      riskTier: 'compliant',
    }
  }

  if (eudrDisplay && riskTierSeverity(eudrTier) > riskTierSeverity(tlTier)) {
    return eudrDisplay
  }

  if (riskTierSeverity(tlTier) > 0) {
    return tlDisplay
  }

  if (eudrDisplay) return eudrDisplay

  return {
    label: trafficLightLabelFromState(baselineTl, translate),
    badgeClass: trafficLightBadgeClassFromState(baselineTl),
    riskTier: 'none',
  }
}

export function isCombinedMediumRiskProductionPlace(item, options = {}) {
  return getMergedProductionPlaceStatusDisplay(item, undefined, options)?.riskTier === 'medium'
}

export function isCombinedHighRiskProductionPlace(item, options = {}) {
  return getMergedProductionPlaceStatusDisplay(item, undefined, options)?.riskTier === 'high'
}
